import { useMemo, useRef } from 'react';
import { useSyncOutside } from 'shared/hooks';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { config } from 'shared/config';

const SESSION_KEY = 'unity_session_id';
const VISITOR_KEY = 'unity_visitor_id';

const getOrCreateId = (storage: Storage, key: string) => {
    let value = storage.getItem(key);
    if (!value) {
        const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        storage.setItem(key, uuid);
        value = uuid;
    }
    return value;
};

const getUserId = () => {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return parsed?.userId ?? null;
    } catch {
        return null;
    }
};

// A public page link carries its credential in the path: no hit may record it, wherever the tracker runs.
const redactedPath = (path: string) => path.replace(/^\/p\/[^/?#]*/, '/p/:token');

const PageHitTracker = () => {
    const location = useLocation();
    const lastSentRef = useRef<string | null>(null);
    const sessionId = useMemo(() => getOrCreateId(sessionStorage, SESSION_KEY), []);
    const visitorId = useMemo(() => getOrCreateId(localStorage, VISITOR_KEY), []);

    useSyncOutside(() => {
        if (['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)) {
            return;
        }
        const path = redactedPath(location.pathname || '/');
        const queryString = path.startsWith('/p/') ? '' : location.search || '';
        const routeKey = `${path}${queryString}`;

        if (lastSentRef.current === routeKey) {
            return;
        }
        lastSentRef.current = routeKey;

        const payload = {
            path,
            queryString,
            fullUrl: `${window.location.origin}${routeKey}`,
            referrer: document.referrer || null,
            title: document.title || null,
            userId: getUserId(),
            sessionId,
            visitorId,
        };

        void axios.post(`${config.API_URL}/api/page-hit`, payload).catch((error) => {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('Page hit tracking failed', error);
            }
        });
    }, [location.pathname, location.search, sessionId, visitorId]);

    return null;
};

export default PageHitTracker;
